'use client'

import React from 'react'
import {useTranslations} from 'next-intl'
import {css, useTheme} from '@emotion/react'

import {ProcessStatus} from '../../../../constants'
import delay from '../../../../utils/delay'
import generateSnowflakeIDStrings from './generateSnowflakeIDStrings'
import ProcessStatusInfo from '../../../../components/modules/ProcessStatusInfo'
import Paper from '../../../../components/elements/Paper'
import NumberField from '../../../../components/elements/NumberField'
import Button from '../../../../components/elements/Button'
import InputResultPanelLayout from '../../../../components/layouts/InputResultPanelLayout'
import DataExportControl from '../../../../components/modules/DataExportControl'
import TextEditor from '../../../../components/modules/TextEditor'


export default function SnowflakeIDGenerator() {
  const theme = useTheme()
  const styles = {
    inputPanel: css`
      padding: 16px;
      background: ${theme.palette.surface.main};

      display: flex;
      flex-direction: column;

      @media (max-width: ${theme.breakpoints.sm}px) {
        padding: 8px;
      }
    `,

    control: css`
      padding: 16px;
      flex: 1;

      @media (max-width: ${theme.breakpoints.sm}px) {
        padding: 8px;
      }
    `,

    numberInput: css`
      margin-top: 8px;
    `,

    bottom: css`
      margin-top: 12px;
      position: relative;
    `,

    button: css`
      width: 100%;
      font-size: 1.3em;
      padding: 4px 16px;
    `,

    status: css`
      position: absolute;
      top: 4px;
      right: 12px;
    `,


    bottomControlBar: css`
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: flex-end;

      height: 36px;
    `,
  }

  const t = useTranslations()

  const [outputResult, setOutputResult] = React.useState('')
  const onOutputResultChange = (outputResult) => {
    setOutputResult(outputResult)
  }

  const [options, setOptions] = React.useState({
    machineID: 10,
    numberOfIDs: 1,
  })
  const onOptionsChange = (options) => {
    setOptions(options)
    setProcessStatus(ProcessStatus.Idle)
  }

  const isValid = (
    options.numberOfIDs > 0
  )

  const [processStatus, setProcessStatus] = React.useState(ProcessStatus.Idle)
  const onGenerateButtonClick = async () => {
    await setProcessStatus(ProcessStatus.Processing)
    await delay(500)

    const ids = await generateSnowflakeIDStrings(options.machineID, options.numberOfIDs)
    let result = ''
    for (let i = 0; i < ids.length; i++) {
      result += ids[i] + '\n'
    }

    await setOutputResult(result)
    await setProcessStatus(ProcessStatus.Success)
  }

  return (
    <InputResultPanelLayout
      inputContent={(
        <Paper css={styles.inputPanel}>
          <Paper css={styles.control}>
            <div css={css`
              display: flex;
              align-items: end;
              gap: 4px;
            `}>
              <NumberField
                label={t('snowflake-id-generator.machine-id')}
                css={styles.numberInput}
                value={options.machineID}
                onChange={value => onOptionsChange({...options, machineID: value})}
              />
              <div css={css`
                font-size: 1.5em;
              `}>×
              </div>
              <NumberField
                label={t('snowflake-id-generator.number-of-ids')}
                css={styles.numberInput}
                value={options.numberOfIDs}
                onChange={value => onOptionsChange({...options, numberOfIDs: value})}
              />
            </div>
          </Paper>

          <div css={styles.bottom}>
            <Button
              css={styles.button}
              disabled={!isValid}
              onClick={onGenerateButtonClick}>
              {t('snowflake-id-generator.generate')}
            </Button>
            <ProcessStatusInfo css={styles.status} enableProgressing processStatus={processStatus}/>
          </div>
        </Paper>
      )}

      resultContent={(
        <TextEditor
          id="text-output"
          loading={processStatus === ProcessStatus.Processing}
          value={outputResult instanceof Error ? outputResult.toString() : outputResult}
          onChange={onOutputResultChange}

          extraControl={(
            <div css={styles.bottomControlBar}>
              <DataExportControl
                disabled={!outputResult || outputResult instanceof Error}
                data={outputResult}

                downloadButtonId={'download-button'}
              />
            </div>
          )}
        />
      )}
    />
  )
}

